import React, { useState, useReducer, useEffect } from "react"
import {
  Card,
  Radio,
  Input,
  Button,
  Checkbox,
  Space,
  Typography,
  Alert,
  Divider,
  Row,
  Col,
  Tag,
  Flex,
  Spin,
  Tooltip,
  Result,
} from "antd"
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  SaveOutlined,
  UserOutlined,
  NumberOutlined,
  PhoneOutlined,
  ShopOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons"
import {
  enumToArray,
  getInspectionStatusEnum,
  serializeQuery,
} from "helpers/service_helper"
import { InspectionParameterType } from "models/types/inspection_parameter_type"
import MapForm from "./MapForm"
import { loadStimulsoftScripts } from "helpers/stimulsoftLoader"

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

export const SubmitSupervision = ({ rowData, mainProps }) => {
  const { onGetProjectFiles, lstGasProjectFiles } = mainProps
  const { t } = mainProps
  
  const [hasError, setHasError] = useState(false)
  const [formInput, setFormInput] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      inspectionStatus: 0,
      description: "",
      defect: false,
      inspectionParameterType: getInspectionStatusEnum(
        rowData.gasProjectDetail.totalUsage
      ),
      packageNeed: "true",
      isWorkDone: "false",
      isPresentExe: "true",
      totalGasUse: rowData.gasProjectDetail.totalUsage,
      totalPipeUse: rowData.totalPipeUse ? rowData.totalPipeUse : 1,
      packageSerialNumber: rowData.packageSerialNumber,
      disapprovalTypeEnum: 0,
    }
  )

  const [inspectionParameterType, setInspectionParameterType] = useState([])

  const resetForm = async () => {
    await onGetProjectFiles(rowData.gasProject.id)
    setFormInput({ description: "" })
    setFormInput({ inspectionStatus: 0 })
    setFormInput({ packageNeed: "true" })
    setFormInput({ isWorkDone: "false" })
    setFormInput({ isPresentExe: "true" })
    setFormInput({ defect: false })
    setFormInput({ inspectionParameterType: 0 })
    setFormInput({ checkedApproved: false })
  }

  const handleInput = (name, value) => {
    setHasError(false)
    setFormInput({ [name]: value })
  }

  const ensureStimulsoftAndInit = async () => {
    await loadStimulsoftScripts()
  }

  useEffect(() => {
    ensureStimulsoftAndInit()
    setInspectionParameterType(enumToArray(InspectionParameterType))
    resetForm()
  }, [])

  const handleSubmitForm = async () => {
    const { onUpdateProcessMapStageNew, onUpdateProcessDefectStageNew } = mainProps

    let mapToSave = {
      ...formInput,
      inspectionStatus: (() => {
        switch (+formInput.inspectionStatus) {
          case 1:
            return 1
          case 2:
            return 2
          case 3:
            return 3
        }
      })(),
      inspectionParameterType: getInspectionStatusEnum(
        rowData.gasProjectDetail.totalUsage
      ),
      solarDateDeliverEngineer: rowData.solarDateDeliverEngineer,
      julianDateDeliverEngineer: rowData.julianDateDeliverEngineer,
      idQuarterTariff: rowData.quarterTariff.id,
      idGasProject: rowData.idGasProject,
      idGasProjectProcess: rowData.id,
      defect:
        +formInput.inspectionStatus === 1
          ? formInput.defect === "true" || formInput.defect === true
          : false,
      packageNeed:
        formInput.packageNeed === "true" || formInput.packageNeed === true,
      isWorkDone:
        formInput.isWorkDone === "true" || formInput.isWorkDone === true,
      isPresentExe:
        formInput.isPresentExe === "true" || formInput.isPresentExe === true,
      totalGasUse: rowData.gasProjectDetail.totalUsage,
      totalPipeUse: rowData.totalPipeUse ? rowData.totalPipeUse : 1,
      packageSerialNumber: formInput.packageSerialNumber,
      description: formInput.description,
      disapprovalTypeEnum: 2,
    }

    if (
      ((mapToSave.inspectionStatus === 1 && mapToSave.defect) ||
        mapToSave.inspectionStatus > 1) &&
      mapToSave.description === ""
    ) {
      alert("توضیحات را وارد کنید")
      return
    }

    if (rowData.projectLevel === 5) {
      if (window.confirm("آیا مطمئن هستید؟")) {
        await onUpdateProcessMapStageNew({
          process: mapToSave,
          queryString: serializeQuery({ idFile: rowData.idFile }),
        })
      }
    } else if (rowData.projectLevel === 3) {
      if (window.confirm("آیا مطمئن هستید؟")) {
        await onUpdateProcessDefectStageNew({
          process: mapToSave,
          queryString: serializeQuery({ idFile: rowData.idFile }),
        })
      }
    }
  }

  // Status options
  const statusOptions = [
    { 
      value: 1, 
      label: "پیش نمایش", 
      icon: <CheckCircleOutlined />,
      color: "#52c41a",
    },
    { 
      value: 2, 
      label: "عدم همکاری", 
      icon: <WarningOutlined />,
      color: "#faad14",
    },
    { 
      value: 3, 
      label: "عدم قبول (کنسل)", 
      icon: <CloseCircleOutlined />,
      color: "#ff4d4f",
    },
  ]

  const canSave = +formInput.inspectionStatus !== 0 &&
    ((rowData.packageNeed && rowData.packageSerialNumber) ||
      !rowData.packageNeed ||
      +formInput.inspectionStatus === 2)

  const isCheckboxRequired = +formInput.inspectionStatus === 1 || +formInput.inspectionStatus === 2

  return (
    <div style={{ padding: "8px 0" }}>
      {/* Status Selection Card */}
      <Card
        size="small"
        title={
          <Space>
            <FileTextOutlined style={{ color: "#1890ff" }} />
            <Text strong>انتخاب وضعیت</Text>
          </Space>
        }
        style={{
          borderRadius: 12,
          marginBottom: 16,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        {/* Warning for status 2 */}
        {+formInput.inspectionStatus === 2 && (
          <Alert
            message="با انتخاب عدم همکاری به میزان یک سوم تعرفه کسر می‌گردد"
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 16, borderRadius: 8 }}
          />
        )}

        {/* Main Status Selection */}
        <Radio.Group
          value={formInput.inspectionStatus}
          onChange={(e) => handleInput("inspectionStatus", e.target.value)}
          style={{ width: "100%" }}
        >
          <Flex wrap="wrap" gap={12}>
            {statusOptions.map(option => (
              <Card
                key={option.value}
                hoverable
                size="small"
                style={{
                  flex: "1 1 150px",
                  borderRadius: 10,
                  border: +formInput.inspectionStatus === option.value 
                    ? `2px solid ${option.color}` 
                    : "1px solid #f0f0f0",
                  background: +formInput.inspectionStatus === option.value 
                    ? `${option.color}10` 
                    : "#fff",
                  cursor: "pointer",
                  transition: "all 0.3s",
                }}
                bodyStyle={{ padding: 12 }}
                onClick={() => handleInput("inspectionStatus", option.value)}
              >
                <Flex align="center" gap={8}>
                  <Radio value={option.value} />
                  <span style={{ 
                    color: +formInput.inspectionStatus === option.value ? option.color : "#595959",
                    fontWeight: +formInput.inspectionStatus === option.value ? 600 : 400,
                  }}>
                    {option.icon} {option.label}
                  </span>
                </Flex>
              </Card>
            ))}
          </Flex>
        </Radio.Group>

        {/* Defect Selection for status 1 */}
        {+formInput.inspectionStatus === 1 && (
          <div style={{ marginTop: 16 }}>
            <Alert
              message="با انتخاب نقص به میزان یک سوم تعرفه کسر می‌گردد"
              type="error"
              showIcon
              icon={<ExclamationCircleOutlined />}
              style={{ marginBottom: 12, borderRadius: 8 }}
            />
            
            <Radio.Group
              value={formInput.defect ? formInput.defect : "false"}
              onChange={(e) => handleInput("defect", e.target.value)}
            >
              <Flex gap={12}>
                <Card
                  hoverable
                  size="small"
                  style={{
                    borderRadius: 10,
                    border: formInput.defect === "false" || !formInput.defect
                      ? "2px solid #52c41a"
                      : "1px solid #f0f0f0",
                    background: formInput.defect === "false" || !formInput.defect
                      ? "#f6ffed"
                      : "#fff",
                    cursor: "pointer",
                  }}
                  bodyStyle={{ padding: "8px 16px" }}
                  onClick={() => handleInput("defect", "false")}
                >
                  <Radio value="false">
                    <Space>
                      <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      <span>تایید</span>
                    </Space>
                  </Radio>
                </Card>
                
                <Card
                  hoverable
                  size="small"
                  style={{
                    borderRadius: 10,
                    border: formInput.defect === "true"
                      ? "2px solid #ff4d4f"
                      : "1px solid #f0f0f0",
                    background: formInput.defect === "true"
                      ? "#fff2f0"
                      : "#fff",
                    cursor: "pointer",
                  }}
                  bodyStyle={{ padding: "8px 16px" }}
                  onClick={() => handleInput("defect", "true")}
                >
                  <Radio value="true">
                    <Space>
                      <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                      <span>نقص</span>
                    </Space>
                  </Radio>
                </Card>
              </Flex>
            </Radio.Group>
          </div>
        )}
      </Card>

      {/* Description Card */}
      <Card
        size="small"
        title={
          <Space>
            <InfoCircleOutlined style={{ color: "#1890ff" }} />
            <Text strong>توضیحات</Text>
          </Space>
        }
        style={{
          borderRadius: 12,
          marginBottom: 16,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <TextArea
          rows={4}
          value={formInput.description || ""}
          onChange={(e) => handleInput("description", e.target.value)}
          placeholder="توضیحات ضروری را وارد کنید..."
          style={{ borderRadius: 8 }}
        />
      </Card>

      {/* Additional Info Section */}
      {(+formInput.inspectionStatus === 1 || +formInput.inspectionStatus === 2) && (
        <>
          {/* Package Info */}
          {rowData.packageNeed && +formInput.inspectionStatus === 1 && (
            <Card
              size="small"
              title={
                <Space>
                  <ShopOutlined style={{ color: "#722ed1" }} />
                  <Text strong>اطلاعات پکیج/آبگرمکن</Text>
                </Space>
              }
              style={{
                borderRadius: 12,
                marginBottom: 16,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                border: rowData.packageSerialNumber 
                  ? "1px solid #d9f7be" 
                  : "1px solid #ffccc7",
              }}
            >
              {rowData.packageSerialNumber ? (
                <Row gutter={[16, 12]}>
                  <Col xs={12} sm={6}>
                    <Flex align="center" gap={8}>
                      <NumberOutlined style={{ color: "#1890ff" }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                          سریال
                        </Text>
                        <Text strong style={{ fontFamily: "tahoma" }}>
                          {rowData.packageSerialNumber}
                        </Text>
                      </div>
                    </Flex>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Flex align="center" gap={8}>
                      <ShopOutlined style={{ color: "#52c41a" }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                          شرکت
                        </Text>
                        <Text strong>{rowData.packageCompanyName}</Text>
                      </div>
                    </Flex>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Flex align="center" gap={8}>
                      <UserOutlined style={{ color: "#722ed1" }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                          نصاب
                        </Text>
                        <Text strong>{rowData.packageInstallerName}</Text>
                      </div>
                    </Flex>
                  </Col>
                 
                </Row>
              ) : (
                <Alert
                  message="شماره سریال وارد نشده است"
                  description="ذخیره غیرفعال است چون نصاب شماره سریال را وارد نکرده است."
                  type="error"
                  showIcon
                  style={{ borderRadius: 8 }}
                />
              )}
            </Card>
          )}

          {/* Map Form */}
          {+formInput.inspectionStatus === 1 && (
            <Card
              size="small"
              style={{
                borderRadius: 12,
                marginBottom: 16,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              bodyStyle={{ padding: 16 }}
            >
              <MapForm
                lstGasProjectFiles={lstGasProjectFiles}
                model={rowData}
              />
            </Card>
          )}

          {/* Confirmation Section */}
          <Card
            size="small"
            style={{
              borderRadius: 12,
              marginBottom: 16,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              background: formInput.checkedApproved ? "#f6ffed" : "#fafafa",
              border: formInput.checkedApproved 
                ? "1px solid #b7eb8f" 
                : "1px solid #f0f0f0",
            }}
          >
            <Flex align="center" gap={12}>
              <Checkbox
                checked={formInput.checkedApproved ?? false}
                onChange={(e) => handleInput("checkedApproved", e.target.checked)}
              />
              <div>
                <Text>اینجانب مهندس </Text>
                <Tag color="blue" style={{ margin: "0 4px" }}>{rowData.enName}</Tag>
                {+formInput.inspectionStatus === 2 && (
                  <Text>نقشه پرونده با مشخصات فوق را <Text strong type="danger">تایید نمی‌نمایم</Text>.</Text>
                )}
                {+formInput.inspectionStatus === 1 && (
                  <Text>
                    نقشه پرونده با مشخصات فوق را{" "}
                    {formInput.defect === "true" ? (
                      <Text strong type="danger">تایید نمی‌نمایم</Text>
                    ) : (
                      <Text strong type="success">تایید می‌نمایم</Text>
                    )}.
                  </Text>
                )}
              </div>
            </Flex>
          </Card>
        </>
      )}

      {/* Alerts & Submit */}
      <Card
        size="small"
        style={{
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        {mainProps.success && (
          <Alert
            message="اطلاعات با موفقیت ذخیره شد"
            type="success"
            showIcon
            style={{ marginBottom: 16, borderRadius: 8 }}
          />
        )}
        {mainProps.error && (
          <Alert
            message={mainProps.error}
            type="error"
            showIcon
            style={{ marginBottom: 16, borderRadius: 8 }}
          />
        )}

        <Flex justify="flex-end" gap={12}>
          {mainProps.loading ? (
            <Spin />
          ) : canSave ? (
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              disabled={isCheckboxRequired && !formInput.checkedApproved}
              onClick={handleSubmitForm}
              style={{
                borderRadius: 8,
                height: 44,
                paddingInline: 32,
                background: formInput.checkedApproved || !isCheckboxRequired
                  ? "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)"
                  : undefined,
                border: "none",
              }}
            >
              ذخیره
            </Button>
          ) : (
            <Tag color="warning" style={{ padding: "8px 16px", fontSize: 13 }}>
              <WarningOutlined style={{ marginLeft: 8 }} />
              ذخیره غیرفعال است
            </Tag>
          )}
        </Flex>
      </Card>
    </div>
  )
}
